import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import decode from 'jwt-decode';
import authApi from '../../services/authApi';
import { redirectUser } from '../../services/user';
import { setAccountType, getLanguage } from '../../services/auth';
import { transcodeLanguage } from '../../utils/language';
import { saveRecentAccount } from '../../utils/recentAccounts';
import CircularLoader from '../../components/CircularLoader';
import { getMarketingParams, getMarketingParamsFromObject } from '../../utils/marketingParams';
import { getLoginCallbackUrl, persistMcpOAuthParams } from '../../utils/mcpOAuthParams';

/**
 * OAuthCallback handles the redirect from OAuth providers (Facebook, Microsoft, Google)
 * when running inside a React Native WebView.
 *
 * The URL will contain query params like:
 *   /oauth/callback?provider=facebook&code=ABC123
 *   /oauth/callback?provider=microsoft&code=ABC123
 *   /oauth/callback?provider=google&code=ABC123
 *
 * This page extracts the code, calls /provider_sign_in, and sends the token
 * back to the React Native app via the Bridge.
 */
export default function OAuthCallback() {
  const history = useHistory();
  const [error, setError] = useState(null);

  useEffect(() => {
    const processCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      let provider = params.get('provider');
      const code = params.get('code');
      const errorParam = params.get('error');

      // Extract provider from state param (used by redirect flows that don't pass provider in query)
      let stateData = {};
      if (params.get('state')) {
        try {
          stateData = JSON.parse(params.get('state'));
          if (!provider && stateData?.provider) provider = stateData.provider;
        } catch {
          /* state is not JSON, ignore */
        }
      }

      if (errorParam) {
        setError(params.get('error_description') || 'Login failed');
        return;
      }

      if (!provider || !code) {
        setError('Missing provider or authorization code');
        return;
      }

      try {
        const redirectUri = params.get('provider')
          ? `${window.location.origin}/oauth/callback?provider=${provider}`
          : `${window.location.origin}/oauth/callback`;
        const language = transcodeLanguage(getLanguage());

        // Skip deep link when the OAuth was initiated from a regular browser session
        // (e.g. Firefox GIS fallback) — indicated by the sessionStorage flag.
        let isBrowserFlow = false;
        try {
          isBrowserFlow = !!sessionStorage.getItem('oauth_browser_flow');
          sessionStorage.removeItem('oauth_browser_flow');
        } catch (e) {
          /* ignore */
        }

        const marketingParams = {
          ...getMarketingParams(),
          ...getMarketingParamsFromObject(stateData),
        };

        persistMcpOAuthParams();

        const payload = {
          provider,
          token: code,
          captcha: process.env.REACT_APP_CAPTCHA_UUID,
          callback_url: getLoginCallbackUrl(),
          language,
          redirect_uri: redirectUri,
          ...marketingParams,
          // Same tratativa as old native login when returning to the mobile app.
          ...(!isBrowserFlow ? { mobile_device: true } : {}),
        };

        if (provider === 'microsoft') {
          payload.user_id = 'microsoft_code_exchange';
        } else if (provider === 'facebook') {
          payload.user_id = 'facebook_code_exchange';
        } else if (provider === 'google') {
          payload.user_id = 'google_code_exchange';
        } else if (provider === 'apple') {
          payload.user_id = 'apple_code_exchange';
        }

        const response = await authApi.post('/provider_sign_in', payload);
        setAccountType(provider);

        const { email: respEmail, name: respName, token: respToken } = response.data || {};
        const accountEmail =
          respEmail ||
          (() => {
            try {
              const p = decode(respToken);
              return p.email || p.username;
            } catch {
              return null;
            }
          })();

        if (accountEmail) {
          saveRecentAccount({
            name: respName || accountEmail.split('@')[0],
            email: accountEmail,
            provider,
          });
        }

        // When opened from the mobile app's external browser (not inside WebView),
        // redirect back to the app via deep link with the token.
        if (!window.ReactNativeWebView && response.data?.token && !isBrowserFlow) {
          const deepLink = `mlabs://connect?token=${encodeURIComponent(response.data.token)}`;
          window.location.href = deepLink;
          return;
        }

        await redirectUser(response);
      } catch (err) {
        const message = err?.response?.data?.message || 'Login failed. Please try again.';
        setError(message);
      }
    };

    processCallback();
  }, []);

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: 24,
        }}
      >
        <p style={{ color: '#d32f2f', marginBottom: 16, textAlign: 'center' }}>{error}</p>
        <button
          type="button"
          onClick={() => history.push('/accounts/sign_in')}
          style={{ padding: '8px 24px', cursor: 'pointer' }}
        >
          Voltar ao login
        </button>
      </div>
    );
  }

  return (
    <div
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}
    >
      <CircularLoader />
    </div>
  );
}
